import React, { PropTypes, Component } from 'react';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import classnames from 'classnames';
import appConfig from 'config';
import { Entitlements, DarkThemeLogos } from 'enums';
import { dateFormat } from 'lib/format';
import { isContentLocked } from 'lib/auth';
import Select from 'common/Select';
import getContentFromListing from 'lib/format/getContentFromListing';
import getListingNameFromStream from 'lib/format/getListingNameFromStream';
import ScheduleLink from 'common/schedule/ScheduleLink';
import StatusFilter from 'common/schedule/StatusFilter';
import ResponsiveTable from '@alias/ResponsiveTable';

const {
    secureCdnHost
} = appConfig;

const networkToImageName = { espn1: 'espn', espndeportes: 'espndeportes_blk' };

function getNetworkImage(abbr, packageNamesArg = [], darkTheme = false) {
    let images = [];
    if (abbr === 'espn_dtc' || abbr === 'bam_dtc' || abbr === 'espn_ppv') {
        let packageNames = packageNamesArg;
        if (!packageNames.length) {
            packageNames = [Entitlements.ESPN_PLUS];
        }
        images = packageNames.sort().map(packageName => {
            let logo;
            let alt;
            if (packageName === Entitlements.ESPN_PLUS) {
                logo = 'espnplus';
                if (darkTheme) {
                    logo += '_wht';
                }
                alt = 'espn+';
            } else if (packageName === Entitlements.ESPN_PLUS_MLB) {
                logo = 'mlb';
                alt = 'mlb.tv';
            } else if (packageName === Entitlements.ESPN_PLUS_NHL) {
                logo = 'nhl';
                if (darkTheme) {
                    logo += '_wht';
                }
                alt = 'nhl.tv';
            // NOTE: Find a match for any `ESPN_PLUS_UFC_PPV_*` entitlements
            } else if (packageName.indexOf(Entitlements.ESPN_PLUS_UFC_PPV) !== -1) {
                logo = 'ufc_ppv';
                alt = 'ufc';

                if (darkTheme) {
                    logo += '_wht';
                }
            }
            const image =
                `${secureCdnHost}/watchespn/images/web/network_logos/channel_logo_${logo}_2x.png`;
            return { image, alt };
        });
    } else {
        let networkLogoName = networkToImageName[abbr] || abbr;
        const networkAlt = abbr;

        if (darkTheme) {
            if (abbr === 'espndeportes') {
                networkLogoName = 'espndeportes';
            } else if (DarkThemeLogos[abbr]) {
                // NOTE: Use the dictionary to determine which logos support darkmode
                networkLogoName = DarkThemeLogos[abbr];
            }
        }

        const networkLogoImageSource =
            `${secureCdnHost}/watchespn/images/web/network_logos/channel_logo_${networkLogoName}_2x.png`;
        images.push({ image: networkLogoImageSource, alt: networkAlt });
    }

    return images;
}

class ScheduleCategorySection extends Component {
    constructor(props) {
        super(props);
        this.state = { selectedSubcategoryId: -1 };
        this.onSubcategorySelect = this.onSubcategorySelect.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.selectedTab !== this.props.selectedTab) {
            this.setState({ selectedSubcategoryId: -1 });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        // Re-Render if:
        // (1) Number of listings changes in a section
        // (2) User authenticates
        // (3) Subcategory filter is used
        // (4) The first listing is different
        return (
            this.props.listings.length !== nextProps.listings.length ||
            this.props.userAuth.length !== nextProps.userAuth.length ||
            this.state.selectedSubcategoryId !== nextState.selectedSubcategoryId ||
            this.props.listings[0].id !== nextProps.listings[0].id ||
            this.props.preauthorizedResources !== nextProps.preauthorizedResources
        );
    }

    componentDidUpdate() {
        if (window.espn_ui) {
            window.espn_ui.LoadBehavior();
        }
    }

    onSubcategorySelect(option) {
        this.setState({ selectedSubcategoryId: option.value });
    }

    render() {
        const {
            category,
            listings,
            preauthorizedResources,
            userAuth,
            selectedTab,
            dateFormat: dateFormatStr,
            defaultLabel,
            blackoutMessage,
            showSectionCaption,
            bucketName
        } = this.props;
        const compareObjectsByName = (objA, objB) => {
            const aName = objA.name.trim().toLowerCase();
            const bName = objB.name.trim().toLowerCase();
            if (aName > bName) {
                return 1;
            } else if (aName < bName) {
                return -1;
            }
            return 0;
        };

        const isReplaySelected = selectedTab === StatusFilter.Options.REPLAY;

        const subcategories = uniqBy(
            listings.map((listing) => listing.subcategories[0]), 'name'
        ).sort(compareObjectsByName);

        // moved the 'undefined' subcat to the top
        if (subcategories[subcategories.length - 1] === undefined) {
            subcategories.pop();
            subcategories.unshift(undefined);
        }

        const listingsGroupedBySubcategory = groupBy(listings,
            (listing) => listing.subcategories[0] && listing.subcategories[0].name);

        const hasDropdown = Object.keys(subcategories.filter((sc) => !!sc)).length > 1;

        const h2ClassName = classnames('table-caption', {
            'has-dropdown': hasDropdown
        });

        const selectOptions = [{ label: defaultLabel, value: -1 }]
            .concat(
                subcategories
                    .filter((item) => !!item) // don't include the "undefined" subcat
                    .map((subcat) => ({ label: subcat.name, value: subcat.name }))
            );

        const select = hasDropdown && (
            <Select
                options={selectOptions}
                menuSizeClass="drop-left sm"
                onChange={this.onSubcategorySelect}
            />
        );
        const columns = isReplaySelected ? 5 : 4;
        const tableChildren = [];

        subcategories.forEach((subcategory) => {
            const subcatgoryNotFiltered =
                this.state.selectedSubcategoryId === -1 ||
                (
                    !!subcategory && this.state.selectedSubcategoryId !== -1 &&
                    this.state.selectedSubcategoryId === subcategory.name
                );
            if (!!subcategory && subcatgoryNotFiltered) {
                tableChildren.push(
                    <thead key={`thead_${tableChildren.length}`}>
                        <tr>
                            <th colSpan={columns} ><span>{subcategory.name}</span></th>
                        </tr>
                    </thead>
                );
            } else {
                // insert a blank <thead> for listings without a subcat
                tableChildren.push(<thead key={`thead_${tableChildren.length}`} />);
            }

            const subcategoryListings =
                listingsGroupedBySubcategory[(subcategory && subcategory.name) || subcategory] || [];

            let filteredSubcategories = [];
            if (subcatgoryNotFiltered) {
                filteredSubcategories = subcategoryListings;
            }

            tableChildren.push((
                <tbody key={`tbody_${tableChildren.length}`}>
                    {filteredSubcategories.map((listing) => {
                        const showLock = isContentLocked(getContentFromListing(listing), userAuth, preauthorizedResources);

                        const key = showLock ? (
                            <span className="icon-font-before icon-lock-solid-before" />
                        ) : null;

                        const networkAbbreviation = listing.broadcasts[0].abbreviation;
                        const packageNames = listing.broadcasts[0].packages;
                        const images = getNetworkImage(
                            networkAbbreviation,
                            packageNames
                        );
                        const imageNode = images && images.map(({ image, alt }) => (
                            <img src={image} alt={alt} />
                        ));
                        const startTime = listing.startTime && dateFormat.toISOString(listing.startTime);
                        const displayTime = startTime && dateFormat.dateFormat(startTime, dateFormatStr);

                        const placement = `schedule-${listing.type}`;
                        const listingContent = getContentFromListing(listing);
                        const listingName = getListingNameFromStream(
                          listing,
                          packageNames,
                          [Entitlements.ESPN_PLUS, Entitlements.ESPN_PLUS_MLB, Entitlements.ESPN_PLUS_NHL]
                        );

                        return (
                            <tr key={`tr_${listing.id}`}>
                                <td className="schedule__time">{displayTime}</td>
                                <td className="schedule__competitors">
                                    <ScheduleLink
                                        content={listingContent}
                                        listing={listing}
                                        name={listingName}
                                        placement={placement}
                                        blackoutMessage={blackoutMessage}
                                        bucketName={bucketName}
                                    />
                                </td>
                                <td className="schedule__auth">{key}</td>
                                <td className="schedule__network">
                                    <div className="schedule__network__content">
                                        {imageNode}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            ));
        });

        return (
            <div key={category.id}>
                {showSectionCaption && (
                    <div>
                        <h2 className={h2ClassName}>
                            <span className="long-caption">
                                {category.name}
                            </span>
                        </h2>
                        {select}
                    </div>
                )}
                <ResponsiveTable>
                    {tableChildren}
                </ResponsiveTable>
            </div>
        );
    }
}

ScheduleCategorySection.defaultProps = {
    showSectionCaption: true
};

ScheduleCategorySection.propTypes = {
    category: PropTypes.object,
    listings: PropTypes.array,
    userAuth: PropTypes.array,
    preauthorizedResources: PropTypes.array,
    selectedTab: PropTypes.string,
    dateFormat: PropTypes.string,
    blackoutMessage: PropTypes.string,
    defaultLabel: PropTypes.string,
    showSectionCaption: PropTypes.bool,
    bucketName: PropTypes.string
};

export default ScheduleCategorySection;



// WEBPACK FOOTER //
// ./app/webapp/schedule/ScheduleCategorySection.js